// [Proposition: documents] Client for /api/documents: documents (uploaded
// files or links to an external drive) attached to a to-do item, a task, a
// milestone or the project, and the project's document register.

const BASE = '/api/documents';

/** What a document is attached to. A milestone's id is "<projectId>:<milestoneId>". */
export type DocTarget = { type: 'todo' | 'task' | 'milestone' | 'project'; id: string | number };

export interface DocItem {
  id: number;
  kind: 'file' | 'link';
  url: string | null;
  name: string;
  mime_type: string | null;
  file_size: number | null;
  uploaded_by_user_id: number | null;
  added_by: string;
  added_at: string;
  /** An audio file sent for transcription (and the text copy of its transcript). */
  transcription_id: number | null;
  transcription_status: string | null;
}

export type AttachedTo = 'todo' | 'task' | 'milestone' | 'task_comment' | 'project_comment' | 'meeting' | 'transcription' | 'project';

export interface RegisterItem {
  id: number;
  kind: 'file' | 'link';
  url: string | null;
  name: string;
  file_size: number | null;
  added_by: string;
  added_by_user_id: number | null;
  added_at: string;
  attached_to: AttachedTo;
  // cancelled: no longer in the planning (n is then null; name from the cancellation, '' if unknown).
  milestone: { id: string; n: number | null; name: string; cancelled: boolean } | null;
  task: { id: number; title: string } | null;
  todo_label: string | null;
  /** Joined to a comment, since re-attached here (still shown in the comment). */
  from_comment: boolean;
  mime_type?: string | null;
  /** An audio file sent for transcription (and the text copy of its transcript). */
  transcription: { id: number; status: string } | null;
}

async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(url, { ...options, credentials: 'include' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw Object.assign(new Error(err.error || 'Request failed'), { code: err.code as string | undefined });
  }
  return res.json();
}

const json = (body: unknown): RequestInit => ({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export const docApi = {
  list: (target: DocTarget) =>
    request<DocItem[]>(`${BASE}?target_type=${target.type}&target_id=${encodeURIComponent(String(target.id))}`),
  upload: (target: DocTarget, files: File[]) => {
    const form = new FormData();
    files.forEach(f => form.append('files', f));
    form.append('target_type', target.type);
    form.append('target_id', String(target.id));
    return request<{ ids: number[] }>(`${BASE}/upload`, { method: 'POST', body: form });
  },
  addLink: (target: DocTarget, url: string, name: string) =>
    request<{ id: number }>(`${BASE}/link`, json({ target_type: target.type, target_id: String(target.id), url, name })),
  remove: (id: number) => request<{ ok: true }>(`${BASE}/${id}`, { method: 'DELETE' }),
  register: (projectId: number) => request<RegisterItem[]>(`${BASE}/projects/${projectId}`),
  attach: (id: number, target: DocTarget) =>
    request<{ ok: true }>(`${BASE}/${id}/attach`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ target_type: target.type, target_id: String(target.id) }) }),
};

/** Where to open a document: the link itself, or the file download. */
export const docHref = (d: { id: number; kind: string; url: string | null }) =>
  d.kind === 'link' && d.url ? d.url : `/api/attachments/${d.id}/download`;
